import { create } from 'zustand';
import { logger } from '@/logging';

// Enums matching backend definitions
export enum NotificationSeverity {
  CRITICAL = 'critical',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  HEALTHY = 'healthy',
}

export enum NotificationCategory {
  SYSTEM = 'system',
  DOCUMENT = 'document',
  RUN = 'run',
  HEALTH = 'health',
  WORKER = 'worker',
}

export interface SystemNotification {
  severity: NotificationSeverity;
  source: string;
  category: NotificationCategory;
  title: string;
  message: string;
  runId?: string;
  docId?: string;
  connectorId?: string;
  workerId?: string;
  moduleId?: string;
  metrics?: Record<string, any>;
  actionable?: boolean;
  suggestedAction?: string;
  autoResolveSeconds?: number;
  errorCode?: string;
  stackTrace?: string;
  timestamp?: string; // Added for tracking when received
}

export interface WorkerHealth {
  moduleId: string;
  workerId: string;
  status: 'healthy' | 'warning' | 'error';
  lastUpdate: string;
  metrics?: {
    cpuPercent?: number;
    memoryMb?: number;
    uptime?: number;
    documentsInProcess?: number;
  };
}

export interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  lastChecked: string;
  details?: string;
}

interface NotificationStore {
  // State
  notifications: SystemNotification[];
  workerHealth: Map<string, WorkerHealth>;
  serviceHealth: Map<string, ServiceHealth>;
  systemMetrics: {
    cosmosDb?: {
      provisionedRUs: number;
      consumedRUs: number;
      utilizationPercent: number;
      isThrottling: boolean;
    };
    queues?: Record<string, { length: number; processing: number }>;
  };
  isInitialized: boolean;
  isInitializing: boolean; // Track initialization in progress
  lastDataUpdate: string | null; // Track data freshness

  // Actions
  addNotification: (notification: SystemNotification) => void;
  updateWorkerHealth: (notification: SystemNotification) => void;
  updateServiceHealth: (notification: SystemNotification) => void;
  updateSystemMetrics: (notification: SystemNotification) => void;
  clearOldNotifications: (maxAge: number) => void;
  clearAllNotifications: () => void; // Clear all stored notifications
  clearStaleHealthData: (maxAgeMs: number) => void; // Clean up old Map entries
  getRecentErrors: (limit?: number) => SystemNotification[];
  getSystemNotifications: (limit?: number) => SystemNotification[]; // All system notifications (non-health)
  getOverallSystemHealth: () => 'healthy' | 'degraded' | 'unhealthy';
  getDataFreshness: () => { isStale: boolean; ageMs: number; lastUpdate: string | null };
  initializeFromAPI: () => Promise<void>;
}

// Maximum entries to keep in health Maps to prevent memory leaks
const MAX_WORKER_ENTRIES = 100;
const MAX_SERVICE_ENTRIES = 50;
const STALE_DATA_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

// LocalStorage keys for persistence
const NOTIFICATIONS_STORAGE_KEY = 'cortexflow-notifications';
const MAX_STORED_NOTIFICATIONS = 50;

// Helper to load notifications from localStorage
const loadStoredNotifications = (): SystemNotification[] => {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    if (!stored) return [];

    const notifications = JSON.parse(stored) as SystemNotification[];
    // Filter out notifications older than 24 hours
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    return notifications.filter(n => (n.timestamp || '') > cutoff);
  } catch (error) {
    logger.warn('Failed to load stored notifications', { error });
    return [];
  }
};

// Helper to save notifications to localStorage
const saveNotifications = (notifications: SystemNotification[]) => {
  try {
    // Only store the most recent notifications
    const toStore = notifications.slice(0, MAX_STORED_NOTIFICATIONS);
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    logger.warn('Failed to save notifications to localStorage', { error });
  }
};

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  // Initial state - load persisted notifications on startup
  notifications: loadStoredNotifications(),
  workerHealth: new Map(),
  serviceHealth: new Map(),
  systemMetrics: {},
  isInitialized: false,
  isInitializing: false,
  lastDataUpdate: null,

  // Add a new notification
  addNotification: (notification) => {
    const timestampedNotification = {
      ...notification,
      timestamp: notification.timestamp || new Date().toISOString(),
    };

    set((state) => {
      const updatedNotifications = [timestampedNotification, ...state.notifications].slice(0, 100); // Keep last 100

      // Persist to localStorage
      saveNotifications(updatedNotifications);

      return { notifications: updatedNotifications };
    });

    logger.debug('Notification added', {
      severity: notification.severity,
      category: notification.category,
      title: notification.title,
    });
  },

  // Update worker health from notifications
  updateWorkerHealth: (notification) => {
    if (notification.category !== NotificationCategory.WORKER) return;

    const workerId = notification.workerId || notification.source;
    const moduleId = notification.moduleId || notification.source.split('-')[0];

    // Log warning if moduleId is missing or defaulting
    if (!notification.moduleId) {
      logger.warn('Worker notification missing moduleId, inferring from source', {
        source: notification.source,
        inferredModuleId: moduleId,
      });
    }

    const health: WorkerHealth = {
      moduleId,
      workerId,
      status: notification.severity === NotificationSeverity.HEALTHY ? 'healthy' :
              notification.severity === NotificationSeverity.WARNING ? 'warning' : 'error',
      lastUpdate: new Date().toISOString(),
      metrics: notification.metrics,
    };

    set((state) => {
      const newHealth = new Map(state.workerHealth);
      newHealth.set(workerId, health);

      // Prevent unbounded growth - remove oldest entries if over limit
      if (newHealth.size > MAX_WORKER_ENTRIES) {
        const sortedEntries = Array.from(newHealth.entries())
          .sort((a, b) => a[1].lastUpdate.localeCompare(b[1].lastUpdate));

        // Remove oldest entries
        const toRemove = sortedEntries.slice(0, newHealth.size - MAX_WORKER_ENTRIES);
        toRemove.forEach(([key]) => newHealth.delete(key));

        logger.debug('Pruned old worker health entries', {
          removed: toRemove.length,
          remaining: newHealth.size,
        });
      }

      return {
        workerHealth: newHealth,
        lastDataUpdate: new Date().toISOString(),
      };
    });
  },

  // Update service health from notifications
  updateServiceHealth: (notification) => {
    if (notification.category !== NotificationCategory.HEALTH) return;

    const serviceName = notification.source;
    const status = notification.severity === NotificationSeverity.HEALTHY ? 'healthy' :
                  notification.severity === NotificationSeverity.WARNING ? 'degraded' : 'unhealthy';

    const health: ServiceHealth = {
      name: serviceName,
      status,
      lastChecked: new Date().toISOString(),
      details: notification.message,
    };

    set((state) => {
      const newHealth = new Map(state.serviceHealth);
      newHealth.set(serviceName, health);

      // Prevent unbounded growth - remove oldest entries if over limit
      if (newHealth.size > MAX_SERVICE_ENTRIES) {
        const sortedEntries = Array.from(newHealth.entries())
          .sort((a, b) => a[1].lastChecked.localeCompare(b[1].lastChecked));

        // Remove oldest entries
        const toRemove = sortedEntries.slice(0, newHealth.size - MAX_SERVICE_ENTRIES);
        toRemove.forEach(([key]) => newHealth.delete(key));

        logger.debug('Pruned old service health entries', {
          removed: toRemove.length,
          remaining: newHealth.size,
        });
      }

      return {
        serviceHealth: newHealth,
        lastDataUpdate: new Date().toISOString(),
      };
    });
  },

  // Update system metrics from notifications
  updateSystemMetrics: (notification) => {
    if (notification.category !== NotificationCategory.SYSTEM || !notification.metrics) return;

    set((state) => ({
      systemMetrics: {
        ...state.systemMetrics,
        ...notification.metrics,
      },
      lastDataUpdate: new Date().toISOString(),
    }));
  },

  // Clear old notifications
  clearOldNotifications: (maxAgeMs) => {
    const cutoff = new Date(Date.now() - maxAgeMs).toISOString();

    set((state) => {
      const filteredNotifications = state.notifications.filter(n =>
        (n.timestamp || '') > cutoff
      );

      // Persist to localStorage
      saveNotifications(filteredNotifications);

      return { notifications: filteredNotifications };
    });
  },

  // Clear stale health data from Maps to prevent memory leaks
  clearStaleHealthData: (maxAgeMs) => {
    const cutoff = new Date(Date.now() - maxAgeMs).toISOString();

    set((state) => {
      // Clean worker health
      const newWorkerHealth = new Map(state.workerHealth);
      let removedWorkers = 0;
      for (const [key, value] of newWorkerHealth.entries()) {
        if (value.lastUpdate < cutoff) {
          newWorkerHealth.delete(key);
          removedWorkers++;
        }
      }

      // Clean service health
      const newServiceHealth = new Map(state.serviceHealth);
      let removedServices = 0;
      for (const [key, value] of newServiceHealth.entries()) {
        if (value.lastChecked < cutoff) {
          newServiceHealth.delete(key);
          removedServices++;
        }
      }

      if (removedWorkers > 0 || removedServices > 0) {
        logger.debug('Cleared stale health data', {
          removedWorkers,
          removedServices,
          remainingWorkers: newWorkerHealth.size,
          remainingServices: newServiceHealth.size,
        });
      }

      return {
        workerHealth: newWorkerHealth,
        serviceHealth: newServiceHealth,
      };
    });
  },

  // Get recent error notifications
  getRecentErrors: (limit = 10) => {
    const state = get();
    return state.notifications
      .filter(n => n.severity === NotificationSeverity.ERROR || n.severity === NotificationSeverity.CRITICAL)
      .slice(0, limit);
  },

  // Get all system notifications (excluding health and worker notifications)
  getSystemNotifications: (limit = 10) => {
    const state = get();
    return state.notifications
      .filter(n =>
        n.category !== NotificationCategory.HEALTH &&
        n.category !== NotificationCategory.WORKER
      )
      .slice(0, limit);
  },

  // Calculate overall system health
  getOverallSystemHealth: () => {
    const state = get();

    // Check worker health
    const workerStatuses = Array.from(state.workerHealth.values()).map(w => w.status);
    const hasUnhealthyWorker = workerStatuses.some(s => s === 'error');
    const hasDegradedWorker = workerStatuses.some(s => s === 'warning');

    // Check service health
    const serviceStatuses = Array.from(state.serviceHealth.values()).map(s => s.status);
    const hasUnhealthyService = serviceStatuses.some(s => s === 'unhealthy');
    const hasDegradedService = serviceStatuses.some(s => s === 'degraded');

    // Check recent critical notifications
    const recentCritical = state.notifications
      .filter(n => n.severity === NotificationSeverity.CRITICAL)
      .filter(n => {
        const age = Date.now() - new Date(n.timestamp || 0).getTime();
        return age < 5 * 60 * 1000; // Last 5 minutes
      });

    if (hasUnhealthyWorker || hasUnhealthyService || recentCritical.length > 0) {
      return 'unhealthy';
    }

    if (hasDegradedWorker || hasDegradedService) {
      return 'degraded';
    }

    return 'healthy';
  },

  // Get data freshness information
  getDataFreshness: () => {
    const state = get();
    const lastUpdate = state.lastDataUpdate;

    if (!lastUpdate) {
      return {
        isStale: true,
        ageMs: Infinity,
        lastUpdate: null,
      };
    }

    const ageMs = Date.now() - new Date(lastUpdate).getTime();
    const isStale = ageMs > STALE_DATA_THRESHOLD_MS;

    return {
      isStale,
      ageMs,
      lastUpdate,
    };
  },

  // Clear all notifications (both memory and localStorage)
  clearAllNotifications: () => {
    set({ notifications: [] });

    // Clear from localStorage
    try {
      localStorage.removeItem(NOTIFICATIONS_STORAGE_KEY);
      logger.debug('All notifications cleared from memory and localStorage');
    } catch (error) {
      logger.warn('Failed to clear notifications from localStorage', { error });
    }
  },

  // Initialize store from API for initial data load
  initializeFromAPI: async () => {
    const state = get();

    // Prevent concurrent initialization
    if (state.isInitialized || state.isInitializing) {
      logger.debug('Notification store already initialized or initializing', {
        isInitialized: state.isInitialized,
        isInitializing: state.isInitializing,
      });

      // Wait for ongoing initialization to complete
      if (state.isInitializing) {
        let attempts = 0;
        while (attempts < 50) { // Max 5 seconds wait
          await new Promise(resolve => setTimeout(resolve, 100));
          const currentState = get();
          if (currentState.isInitialized) {
            return;
          }
          attempts++;
        }
        logger.warn('Initialization timeout - proceeding anyway');
      }
      return;
    }

    // Mark as initializing
    set({ isInitializing: true });

    try {
      // Import apiClient to fetch initial data
      const { apiClient } = await import('@/services/api/client');

      // Fetch initial queue statistics for system metrics
      try {
        const queueData = await apiClient.get('/health/queues');
        const queues: Record<string, { length: number; processing: number }> = {};

        if (queueData.data?.queues) {
          for (const queue of queueData.data.queues) {
            queues[queue.name] = {
              length: queue.active_message_count || 0,
              processing: 0, // Not available from this API
            };
          }
        }

        set((state) => ({
          systemMetrics: {
            ...state.systemMetrics,
            queues,
          },
          lastDataUpdate: new Date().toISOString(),
        }));
      } catch (error) {
        logger.warn('Failed to fetch initial queue statistics', { error });
      }

      // Mark as initialized
      set({
        isInitialized: true,
        isInitializing: false,
      });
      logger.debug('Notification store initialized from API');

    } catch (error) {
      logger.error('Failed to initialize notification store from API', { error });
      // Still mark as initialized to prevent infinite retries
      set({
        isInitialized: true,
        isInitializing: false,
      });
    }
  },
}));
